/**
 * Short-lived browser persistence for missing-supplier audit results so users can switch
 * tabs and return without re-running the scan. TTL matches backend proposal_audit_runs (6h).
 */

export const MISSING_SUPPLIER_AUDIT_STORAGE_KEY = 'family_finance_missing_supplier_audit_v1';

const TTL_MS = 6 * 60 * 60 * 1000;

export type StoredMissingSupplierAudit = {
  savedAt: number;
  payload: Record<string, unknown>;
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

export function saveMissingSupplierAuditSnapshot(payload: Record<string, unknown>): void {
  try {
    const rec: StoredMissingSupplierAudit = { savedAt: Date.now(), payload };
    localStorage.setItem(MISSING_SUPPLIER_AUDIT_STORAGE_KEY, JSON.stringify(rec));
  } catch {
    // quota / private mode
  }
}

export function loadMissingSupplierAuditSnapshot(): StoredMissingSupplierAudit | null {
  try {
    const raw = localStorage.getItem(MISSING_SUPPLIER_AUDIT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) return null;
    const savedAt = Number(parsed.savedAt);
    if (!Number.isFinite(savedAt)) return null;
    const payload = parsed.payload;
    if (!isRecord(payload)) return null;
    if (Date.now() - savedAt > TTL_MS) {
      localStorage.removeItem(MISSING_SUPPLIER_AUDIT_STORAGE_KEY);
      return null;
    }
    return { savedAt, payload };
  } catch {
    return null;
  }
}

export function clearMissingSupplierAuditSnapshot(): void {
  try {
    localStorage.removeItem(MISSING_SUPPLIER_AUDIT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function missingSupplierAuditTtlMs(): number {
  return TTL_MS;
}

export function formatMissingSupplierAuditTtlRemaining(savedAt: number): string {
  const ttlMs = TTL_MS;
  const left = ttlMs - (Date.now() - savedAt);
  if (left <= 0) return 'Expired';
  const m = Math.floor(left / 60000);
  if (m < 60) return `${m}m left`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m left`;
}
